// yakuName — map a yaku id (ASCII slug, e.g. 'sanankou') to its kanji display name (name.ja) using
// the working catalog. Internal IDs stay ASCII (stable keys); only the DISPLAY is kanji (Option A,
// DECISIONS.md §5). Falls back to the id when the catalog has no entry (e.g. a stale custom id).
import type { YakuDefMeta } from '@mj/protocol';

export function makeNameOf(catalog: YakuDefMeta[]): (id: string) => string {
  return (id: string) => catalog.find((d) => d.id === id)?.name.ja ?? id;
}

// Some yaku have a distinct DOUBLE name (四暗刻 -> 四暗刻単騎, 国士 -> 国士無双十三面待ち, 九蓮 ->
// 純正九蓮宝燈). That name lives as DATA on the catalog entry (`doubleName`), NOT hardcoded here, so it
// stays editable. When a yaku is scored as a double (mult>=2) we show it. (Always-double yakuman like
// 大四喜/大七星 carry their full name in `name` and need no doubleName.)
function doubleNameOf(catalog: YakuDefMeta[], id: string): string | undefined {
  return catalog.find((d) => d.id === id)?.doubleName?.ja;
}

// Name for a Verify yakuman row, where the multiplier is known directly.
export function makeYakumanName(catalog: YakuDefMeta[]): (id: string, mult: number) => string {
  const base = makeNameOf(catalog);
  return (id, mult) => (mult >= 2 ? doubleNameOf(catalog, id) ?? base(id) : base(id));
}

// Name for a RESULT yaku row, which carries han = 13×mult for yakuman entries (so han>=26 ⇒ ×2).
export function makeResultName(catalog: YakuDefMeta[]): (id: string, han: number) => string {
  const base = makeNameOf(catalog);
  return (id, han) => (han >= 26 ? doubleNameOf(catalog, id) ?? base(id) : base(id));
}
